/**
 * Modern RTK Auth Slice
 * Single source of truth for authentication state
 * Automatically syncs with RTK Query auth endpoints
 */
import { createSlice } from "@reduxjs/toolkit";
import { authApi } from "../../store/api/authApi";

const determineRole = (isAdmin, isClientAdmin) => {
  if (isAdmin === "true" || isAdmin === 1 || isAdmin === "1") return "Admin";
  if (isClientAdmin === "true" || isClientAdmin === 1 || isClientAdmin === "1")
    return "Client Admin";
  return "Regular User";
};

const initialState = {
  user: null,
  userId: null,
  clientId: null,
  role: null,
  isAuthenticated: false,
  token: null,
  isInitializing: true,
};

const resetAuthState = (state) => {
  state.user = null;
  state.userId = null;
  state.clientId = null;
  state.role = null;
  state.isAuthenticated = false;
  state.token = null;
};

const applyAuthenticatedState = (state, payload) => {
  const { user, token } = payload || {};

  if (!user || !token) {
    return false;
  }

  state.user = user;
  state.userId = user.userId ?? user.user_id ?? null;
  state.clientId = user.client_id ?? user.clientId ?? null;
  state.role = determineRole(user.isAdmin, user.isClientAdmin);
  state.isAuthenticated = true;
  state.token = token;
  state.isInitializing = false;

  sessionStorage.setItem("token", token);
  return true;
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      Object.assign(state, initialState);
      state.isInitializing = false;

      sessionStorage.removeItem("token");

      const keysToRemove = [
        "user",
        "user_id",
        "client_id",
        "isAdmin",
        "isClientAdmin",
      ];
      keysToRemove.forEach((key) => {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      });

      window.location.reload();
    },

    restoreAuth: (state, action) => {
      const { user } = action.payload;
      state.user = user;
      state.userId = user.userId;
      state.clientId = user.client_id;
      state.role = determineRole(user.isAdmin, user.isClientAdmin);
      state.isAuthenticated = true;
      state.token = action.payload.token;
      state.isInitializing = false;
    },

    authInitComplete: (state) => {
      state.isInitializing = false;
    },
  },

  extraReducers: (builder) => {
    builder.addMatcher(
      authApi.endpoints.signin.matchFulfilled,
      (state, { payload }) => {
        if (payload?.requiresTwoFactor) {
          resetAuthState(state);
          state.isInitializing = false;
          sessionStorage.removeItem("token");
          return;
        }

        const isApplied = applyAuthenticatedState(state, payload);

        if (!isApplied) {
          resetAuthState(state);
          state.isInitializing = false;
          sessionStorage.removeItem("token");
        }
      },
    );

    builder.addMatcher(
      authApi.endpoints.verify2FA.matchFulfilled,
      (state, { payload }) => {
        const isApplied = applyAuthenticatedState(state, payload);
        if (!isApplied) {
          resetAuthState(state);
          state.isInitializing = false;
          sessionStorage.removeItem("token");
        }
      },
    );

    builder.addMatcher(authApi.endpoints.signin.matchRejected, (state) => {
      resetAuthState(state);
      state.isInitializing = false;
      sessionStorage.removeItem("token");
    });

    builder.addMatcher(authApi.endpoints.logout.matchFulfilled, (state) => {
      resetAuthState(state);
      state.isInitializing = false;
    });
  },
});

export const { logout, restoreAuth, authInitComplete } = authSlice.actions;

export const selectAuth = (state) => state.auth;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectUser = (state) => state.auth.user;
export const selectUserId = (state) => state.auth.userId;
export const selectClientId = (state) => state.auth.clientId;
export const selectRole = (state) => state.auth.role;
export const selectToken = (state) => state.auth.token;
export const selectIsInitializing = (state) => state.auth.isInitializing;

export const selectIsAdmin = (state) => state.auth.role === "Admin";
export const selectIsClientAdmin = (state) =>
  state.auth.role === "Client Admin";
export const selectIsRegularUser = (state) =>
  state.auth.role === "Regular User" || !state.auth.role;

export default authSlice.reducer;
